import React, { useEffect, useRef, useState } from "react";
import { Col, Container, Modal, Pagination, Row } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import {
  ActionIconContainer,
  AddFormButton,
  FilterButton,
  RowOptionsButtonContainer,
} from "../../../shared/components/TableElements";
import {
  CheckBoxInput,
  FormItem,
} from "../../../shared/components/form/FormElements";
import { formInputStyles } from "../../../shared/constants/styles";
import styled from "styled-components";
import {
  colorBorder,
  colorGray,
  colorLightGray,
  colorRowHover,
  scrollbarStyles,
} from "../../../utils/palette";
import { RowOptionsMenuWrap } from "../../../shared/components/table/components/RowOptions";
import { OptionsMenu } from "../../../shared/components/table/components/MoreOptions/MoreOptions";
import useClickAway from "../../../shared/custom-hooks/useClickAway";
import { post, postApi } from "../../../api/getUser";
import PartsFormPopup from "../Parts/PartsFormPopup";
import { TableSpinner } from "../../../shared/components/table/components/ReactTableConstructor";
import { useAuth } from "../../../contexts/AuthContext";
import Error from "@/shared/components/form/Error";
import { useSelector } from "react-redux";
import {
  PaginationInfo,
  PaginationWrap,
} from "../../../shared/components/table/components/ReactTablePagination";
import ChevronRightIcon from "mdi-react/ChevronRightIcon";
import ChevronDoubleRightIcon from "mdi-react/ChevronDoubleRightIcon";
import ChevronLeftIcon from "mdi-react/ChevronLeftIcon";
import ChevronDoubleLeftIcon from "mdi-react/ChevronDoubleLeftIcon";
import useDebounce from "../../../shared/custom-hooks/useDebounce";
import { borderRadius, position } from "polished";
import SubscribePopup from "../../../shared/components/SubscribePopup";
import { NATEXT } from "../../../shared/constants";

// Refactored version — uses taskBatches + onBatchesChange instead of
// partBatches/setPartBatches/serviceTasksRef.
const AddPartsPopupNew = ({
  showPopup,
  setShowPopup,
  serviceTaskDetailId,
  serviceTasks,
  setServiceTasks,
  taskBatches = [], // current task's batches array (replaces partBatches[detailId])
  onNewBatchesReady, // (newBatches: array) => void — called before opening UnitsConsumedPopup
  setShowUnitsConsumedPopup,
  setPartRowData,
  fromUnitsConsumed,
  setFromUnitsConsumed,
  vehicle = { veh_id: "All" },
  // Service task template mode
  fromServiceTask,
  onPartsSubmitted,
  // Suggested part preselection (UseButton flow)
  suggestedPart = null,
}) => {
  const { t } = useTranslation("common");
  const { userId } = useSelector((state) => state.user);
  const { reload, setReload, tag } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [errMsg, setErrMsg] = useState(false);

  //Pagination
  const pageSize = 10;
  const [pageIndex, setPageIndex] = useState(0);
  const [pageIndexArr, setPageIndexArr] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sorting, setSorting] = useState("desc");
  const [filterData, setFilterData] = useState({
    index: 0,
    sorting: "desc",
    search: "",
  });

  const [allBatches, setAllBatches] = useState([]);

  const [filterText, setFilterText] = useState(suggestedPart?.part_name || "");
  const debounceFilterText = useDebounce(filterText, 500);

  const [showCreateNewPart, setShowCreateNewPart] = useState(false);

  const [selectedBatchId, setSelectedBatchId] = useState("");

  const [showPartsForm, setShowPartsForm] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState(false);
  const [batchRowData, setBatchRowData] = useState(false);
  const [mode, setMode] = useState("add-batch");

  const [selectedBatches, setSelectedBatches] = useState([]);
  const selectedBatchIds = selectedBatches?.map((b) => b?.parts_batch_id);

  //State to show subscribe popup
  const [showSubscribePopup, setShowSubscribePopup] = useState(false);

  // Banner for suggested part preselection flow: 'info' | 'warning' | null
  const [bannerType, setBannerType] = useState(null);
  const hasCheckedSuggestedRef = useRef(false);
  const searchInputRef = useRef(null);
  const [subscribePopupErrMsg, setsubscribePopupErrMsg] = useState("");

  const fetchPartsData = async (index = 0) => {
    setIsLoading(true);
    // Use taskBatches directly — no partBatches map lookup needed
    const existingBatches = taskBatches || [];
    const skipBatches =
      existingBatches.length > 0
        ? existingBatches.map((b) => b.parts_batch_id)
        : [];

    const partsApiJson = {
      search: debounceFilterText || "",
      order: sorting,
      querystring: "get_all_parts_batches_data",
      page: index + 1,
      limit: pageSize,
      batch_ids_skip: skipBatches,
      veh_id: vehicle.veh_id,
    };
    const partsApiRes = await post("listingV2", partsApiJson);
    if (partsApiRes?.success) {
      const partsBatchData = partsApiRes?.data;
      const totalRecords = partsApiRes?.recordsTotal;
      setTotalRecords(totalRecords);
      const noOfPages = Math.ceil(totalRecords / pageSize);
      setPageIndexArr([...Array(noOfPages).keys()]);
      setAllBatches(partsBatchData);
      setIsLoading(false);

      // Auto-select and show banner for suggested part (only on first fetch)
      if (suggestedPart && !hasCheckedSuggestedRef.current) {
        hasCheckedSuggestedRef.current = true;
        const match = partsBatchData.find(
          (b) => b.part_id === suggestedPart.part_id,
        );
        if (match) {
          setSelectedBatches([match]);
          setBannerType("info");
        } else {
          setBannerType("warning");
        }
      }
    }
  };

  useEffect(() => {
    (async () => {
      await fetchPartsData();
    })();
  }, [debounceFilterText, sorting]);

  // Focus search input when popup opens with a suggested part pre-filled
  useEffect(() => {
    if (suggestedPart && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [suggestedPart]);

  //Reload Effect
  useEffect(() => {
    if (reload === "parts") {
      (async () => {
        setReload("");
        await fetchPartsData();
      })();
    }
  }, [reload]);

  //Effect to toggle create new part
  useEffect(() => {
    if (!debounceFilterText) {
      setShowCreateNewPart(false);
    }
    if (debounceFilterText) {
      const partNamesArr = allBatches.map((p) =>
        p.part_name?.toLocaleLowerCase(),
      );
      const partNumbersArr = allBatches.map((p) =>
        p.part_number?.toLocaleLowerCase(),
      );
      if (
        !partNamesArr.includes(debounceFilterText.toLocaleLowerCase()) &&
        !partNumbersArr.includes(debounceFilterText.toLocaleLowerCase())
      ) {
        setShowCreateNewPart(true);
      } else {
        setShowCreateNewPart(false);
      }
    }
  }, [debounceFilterText, allBatches]);

  const RowOptions = ({
    batch,
    selectedBatchId,
    setSelectedBatchId,
    setBatchRowData,
    setMode,
    setShowPartsForm,
  }) => {
    const [toggleOptions, setToggleOptions] = useState(false);

    const clickAwayRef = useClickAway({
      handleCloseElement: () => setToggleOptions(false),
    });

    const handleRowOptionsClick = (e) => {
      e.preventDefault();
      setToggleOptions(!toggleOptions);
    };

    const onAddBatchClick = () => {
      setBatchRowData(batch);
      setMode("add-batch");
      setShowPartsForm(true);
    };

    return (
      <>
        <div
          className="d-flex align-items-start justify-content-center position-relative"
          ref={clickAwayRef}
        >
          <RowOptionsButtonContainer
            id="row-options-button"
            style={{ marginTop: 0 }}
            variant="outline-secondary"
            size="sm"
            onClick={(e) => handleRowOptionsClick(e)}
          >
            <i className="fi fi-rr-menu-dots-vertical"></i>
          </RowOptionsButtonContainer>
          {toggleOptions && (
            <RowOptionsMenu
              batch={batch}
              setSelectedBatchId={setSelectedBatchId}
              onAddBatchClick={onAddBatchClick}
            />
          )}
        </div>
      </>
    );
  };

  const RowOptionsMenu = ({ onAddBatchClick }) => {
    const handleAddBatch = () => {
      onAddBatchClick();
    };
    return (
      <RowOptionsMenuWrap
        id="row-options"
        style={{ position: "absolute", right: "0.4rem" }}
      >
        <OptionsMenu id="add-batch" onClick={handleAddBatch}>
          {t("add_batch")}
        </OptionsMenu>
      </RowOptionsMenuWrap>
    );
  };

  const handleFilterTextChange = (e) => {
    const { value } = e.target;
    setFilterText(value);
    setPageIndex(0);
  };

  const handleBatchClick = (e, batch) => {
    if (
      e.target.tagName !== "SPAN" &&
      e.target.tagName !== "I" &&
      e.target.tagName !== "BUTTON"
    ) {
      setSelectedBatches((prev) => {
        if (prev.length === 0) {
          return [batch];
        } else {
          let temp = [...prev];
          const item = temp.find(
            (b) => b.parts_batch_id === batch.parts_batch_id,
          );
          if (item) {
            temp = temp.filter((b) => b.parts_batch_id !== item.parts_batch_id);
          } else {
            temp.push(batch);
          }
          return temp;
        }
      });
      setErrMsg("");
    }
  };

  const handleClose = () => {
    if (fromServiceTask) {
      setShowPopup(false);
      return;
    }
    if (fromUnitsConsumed) {
      setShowUnitsConsumedPopup(true);
      setFromUnitsConsumed(false);
    }
    setShowPopup(false);
  };

  const handleCreateNewPart = async () => {
    if (tag === "free-user") {
      const noOfPartsRes = await postApi(
        { querystring: "get_parts_count" },
        "inventory",
      );
      if (noOfPartsRes?.success && noOfPartsRes?.inventory_data >= 3) {
        setsubscribePopupErrMsg(t("no_of_parts_limit_err"));
        setShowSubscribePopup(true);
        return;
      }
    }

    if (tag === "essential-user") {
      setsubscribePopupErrMsg(t("subscribe_to_advanced_msg"));
      setShowSubscribePopup(true);
      return;
    }
    setBatchRowData({ part_name: debounceFilterText });
    setMode("add-part");
    setShowPartsForm(true);
  };

  const handleSubmit = () => {
    if (selectedBatches.length === 0) {
      setErrMsg(t("one_batch_reqd_err"));
      return;
    }

    if (fromServiceTask) {
      onPartsSubmitted(selectedBatches);
      setShowPopup(false);
      return;
    }

    if (fromUnitsConsumed) {
      setFromUnitsConsumed(false);
    }

    const newBatches = selectedBatches.map((b, i) => ({
      ...b,
      service_task_detail_id: serviceTaskDetailId.current,
      pc_id: `${userId}_${Date.now() + i}`,
      availableQuantity: b.available_quantity,
      currentlyAvailableQuantity: b.available_quantity,
      totalCost: "",
      unitsConsumed: "",
      action: 1,
    }));

    // Pass only the new batches — parent will forward to UnitsConsumedPopup as pendingBatches
    onNewBatchesReady(newBatches);

    setShowUnitsConsumedPopup(true);
    setShowPopup(false);
  };

  return (
    <>
      {showSubscribePopup && (
        <SubscribePopup
          showPopup={showSubscribePopup}
          setShowPopup={setShowSubscribePopup}
          message={subscribePopupErrMsg}
        />
      )}
      {showPartsForm && (
        <PartsFormPopup
          showPopup={showPartsForm}
          setShowPopup={setShowPartsForm}
          mode={mode}
          rowData={batchRowData}
          setSnackbarMsg={setSnackbarMsg}
        />
      )}
      <Modal show={showPopup} size="xl" style={{ zIndex: 999 }}>
        <Modal.Header>
          <h4>
            {fromServiceTask ? t("select_suggested_parts_title") : t("parts")}
          </h4>
          <div className="d-flex justify-content-end">
            <div className="d-flex align-items-center gap-2">
              <AddFormButton type="button" onClick={handleCreateNewPart}>
                {t("add_part")}
              </AddFormButton>
              <ActionIconContainer onClick={handleClose}>
                <i className="fi fi-rr-cross-small" />
              </ActionIconContainer>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="m-0 p-0">
          {bannerType === "info" && suggestedPart && (
            <InfoBanner>
              <i
                className="fi fi-rr-info"
                style={{ position: "relative", top: "2px", flexShrink: 0 }}
              />
              <span>
                {t("suggested_part_preselected_msg", {
                  partName: suggestedPart.part_number
                    ? `${suggestedPart.part_name} \u2014 ${suggestedPart.part_number}`
                    : suggestedPart.part_name,
                })}
              </span>
            </InfoBanner>
          )}
          {bannerType === "warning" && suggestedPart && (
            <WarningBanner>
              <i
                className="fi fi-rr-triangle-warning"
                style={{ position: "relative", top: "2px", flexShrink: 0 }}
              />
              <span>
                {t("suggested_part_not_found_msg", {
                  partName: suggestedPart.part_name,
                  vehicleName:  vehicle.name,
                })}
              </span>
            </WarningBanner>
          )}
          <Container className="mt-4 mb-3">
            <Row className="d-flex justify-content-center align-items-center">
              <Col md={8}>
                <FormItem>
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={filterText}
                    onChange={handleFilterTextChange}
                    style={formInputStyles}
                    placeholder={t("search_parts")}
                    onKeyDown={(e) =>
                      e.key === "Enter" &&
                      debounceFilterText &&
                      showCreateNewPart
                        ? handleCreateNewPart()
                        : null
                    }
                  />
                </FormItem>
              </Col>
            </Row>
          </Container>
          <Container className="m-0 p-0">
            <Row>
              <Col md={3}>
                <div className="mx-3 d-flex gap-1 align-items-center">
                  <p style={{ fontSize: "12px", fontWeight: 300 }}>
                    {t("part_name")}
                  </p>
                  <i
                    className={`fi fi-rr-angle-small-${
                      sorting === "desc" ? "down" : "up"
                    }`}
                    style={{
                      cursor: "pointer",
                      position: "relative",
                      top: "2px",
                    }}
                    onClick={() => {
                      setSorting((prev) => (prev === "desc" ? "asc" : "desc"));
                      setPageIndex(0);
                    }}
                  />
                </div>
              </Col>
              <Col md={2}>
                <p style={{ fontSize: "12px", fontWeight: 300 }}>
                  {t("part_no")}
                </p>
              </Col>
              <Col md={2}>
                <p style={{ fontSize: "12px", fontWeight: 300 }}>
                  {t("batch_id")}
                </p>
              </Col>
              <Col md={2}>
                <p style={{ fontSize: "12px", fontWeight: 300 }}>
                  {t("warehouse")}
                </p>
              </Col>
              <Col md={1}>
                <p style={{ fontSize: "12px", fontWeight: 300 }}>
                  {t("qty_tv")}
                </p>
              </Col>
              <Col md={1}>
                <p style={{ fontSize: "12px", fontWeight: 300 }}>
                  {t("cost_per_unit")}
                </p>
              </Col>
              <Col md={1}></Col>
            </Row>
            <RowContainer $height={`${window.innerHeight - 380}px`}>
              {isLoading ? (
                <div className="text-center">
                  <TableSpinner animation="border" />
                </div>
              ) : (
                <>
                  {allBatches.length === 0 && (
                    <Row
                      className="d-flex justify-content-center align-items-center"
                      style={{ height: "-webkit-fill-available" }}
                    >
                      <Col md={2}>
                        <p>{t("no_records")}</p>
                      </Col>
                    </Row>
                  )}
                  {allBatches.map((batch) => (
                    <PartRow
                      key={batch.parts_batch_id}
                      onClick={(e) => handleBatchClick(e, batch)}
                    >
                      <Col md={3}>
                        <div className="d-flex gap-1">
                          <CheckBoxInput
                            type="checkbox"
                            checked={selectedBatchIds?.includes(
                              batch?.parts_batch_id,
                            )}
                            readOnly
                          />
                          <p>{batch.part_name}</p>
                        </div>
                      </Col>
                      <Col md={2}>
                        <p>{batch.part_number || NATEXT}</p>
                      </Col>
                      <Col md={2}>
                        <p>{batch.batch_id}</p>
                      </Col>
                      <Col md={2}>
                        <p style={{ marginLeft: "0.5rem" }}>{batch.location}</p>
                      </Col>
                      <Col md={1}>
                        <p style={{ marginLeft: "1rem" }}>
                          {batch.available_quantity}
                        </p>
                      </Col>
                      <Col md={1}>
                        <p style={{ marginLeft: "1.5rem" }}>
                          {batch.purchase_cost_per_unit}
                        </p>
                      </Col>
                      <Col md={1}>
                        <RowOptions
                          batch={batch}
                          selectedBatchId={selectedBatchId}
                          setSelectedBatchId={setSelectedBatchId}
                          setBatchRowData={setBatchRowData}
                          setShowPartsForm={setShowPartsForm}
                          setShowPopup={setShowPopup}
                          setMode={setMode}
                        />
                      </Col>
                    </PartRow>
                  ))}
                </>
              )}
            </RowContainer>
            {errMsg && (
              <Row className="mx-1 my-0">
                <Col>
                  <Error error={errMsg} />
                </Col>
              </Row>
            )}
            <PaginationWrap className="my-2 mx-2 p-0">
              <Pagination>
                <Pagination.First
                  disabled={pageIndex === 0}
                  className="pagination__item--arrow"
                  onClick={async () => {
                    setPageIndex(0);
                    await fetchPartsData(0);
                  }}
                >
                  <i className="fi fi-rr-angle-double-small-left"></i>
                </Pagination.First>
                <Pagination.Prev
                  disabled={pageIndex === 0}
                  className="pagination__item--arrow"
                  onClick={async () => {
                    let temp = pageIndex;
                    setPageIndex((prev) => prev - 1);
                    await fetchPartsData(temp - 1);
                  }}
                >
                  <i className="fi fi-rr-angle-small-left"></i>
                </Pagination.Prev>
                {pageIndexArr.map((i) => (
                  <Pagination.Item
                    id="page"
                    key={i}
                    active={pageIndex === i}
                    onClick={async () => {
                      setPageIndex(i);
                      await fetchPartsData(i);
                    }}
                  >
                    {i + 1}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  disabled={pageIndex === pageIndexArr.length - 1}
                  className="pagination__item--arrow"
                  onClick={async () => {
                    let temp = pageIndex;
                    setPageIndex((prev) => prev + 1);
                    await fetchPartsData(temp + 1);
                  }}
                >
                  <i className="fi fi-rr-angle-small-right"></i>
                </Pagination.Next>
                <Pagination.Last
                  disabled={pageIndex === pageIndexArr.length - 1}
                  className="pagination__item--arrow"
                  onClick={async () => {
                    setPageIndex(pageIndexArr.length - 1);
                    await fetchPartsData(pageIndexArr.length - 1);
                  }}
                >
                  <i className="fi fi-rr-angle-double-small-right"></i>
                </Pagination.Last>
              </Pagination>
              <PaginationInfo>
                {t("showing_from_to_no_of_rows", {
                  from: totalRecords > 0 ? pageSize * pageIndex + 1 : 0,
                  to: Math.min(pageSize * (pageIndex + 1), totalRecords),
                  noOfRows: totalRecords,
                })}
              </PaginationInfo>
            </PaginationWrap>
          </Container>
        </Modal.Body>
        <Modal.Footer>
          {fromServiceTask && (
            <p style={{ flex: 1, margin: 0, fontSize: "13px", color: "#888" }}>
              {t("select_parts_to_suggest_msg")}
            </p>
          )}
          {fromServiceTask && (
            <FilterButton type="button" onClick={handleClose}>
              {t("cancel")}
            </FilterButton>
          )}
          <AddFormButton
            id="submit-form-button"
            type="button"
            onClick={handleSubmit}
            disabled={allBatches.length === 0}
          >
            {fromServiceTask && selectedBatches.length > 0
              ? `${t("submit")} - ${selectedBatches.length} ${t("parts").toLowerCase()}`
              : t("submit")}
          </AddFormButton>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AddPartsPopupNew;

//styles

const PartRow = styled(Row)`
  cursor: pointer;
  padding: 0.5rem 0.25rem 0.5rem 0.25rem;
  display: flex;
  align-items: center;

  &:hover {
    background: ${colorRowHover};
  }
`;

export const RowContainer = styled(Container)`
  overflow: auto;
  height: ${(p) => p.$height};
  margin: 0;
  ${scrollbarStyles}
`;

const NewPartContainer = styled.div`
  cursor: pointer;
  padding: 0.35rem 0.5rem;
  display: flex;
  align-items: center;
  gap: 4px;
  border: 1px solid ${colorLightGray};
  border-radius: 5px;
  background: ${colorRowHover};

  &:active {
    background: ${colorLightGray};
  }
`;

const InfoBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 12px 16px 0;
  padding: 10px 14px;
  background: #e8f4fd;
  border: 1px solid #bee3f8;
  border-radius: 6px;
  font-size: 13px;
  color: #1a5276;
`;

const WarningBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 12px 16px 0;
  padding: 10px 14px;
  background: #fffbea;
  border: 1px solid #f6d860;
  border-radius: 6px;
  font-size: 13px;
  color: #7d5a00;
`;
